/*
  Home page redesign behaviour (see assets/hp-home.css). Loaded by every hp-* section, so it guards
  against running twice. Re-binds when a section is re-rendered in the theme editor.
*/
(() => {
  if (window.hpHome) return;
  window.hpHome = true;

  const mobile = window.matchMedia('(max-width: 767px)');

  /* Rail arrows: scroll by one card, disable at the ends */
  const initRails = (root) => {
    root.querySelectorAll('[data-hp-rail-nav]').forEach((nav) => {
      if (nav.dataset.hpBound) return;
      nav.dataset.hpBound = 'true';
      const rail = document.getElementById(nav.dataset.hpRailNav);
      if (!rail) return;
      const [prev, next] = nav.querySelectorAll('.hp-arrow');
      const step = () => {
        const card = rail.firstElementChild;
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        return card ? card.getBoundingClientRect().width + gap : rail.clientWidth;
      };
      const update = () => {
        const max = rail.scrollWidth - rail.clientWidth - 2;
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= max;
      };
      nav.addEventListener('click', (e) => {
        const btn = e.target.closest('.hp-arrow');
        if (btn) rail.scrollBy({ left: step() * Number(btn.dataset.dir), behavior: 'smooth' });
      });
      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  /* Sticky mobile CTA: show once the hero CTA has scrolled away, hide over the footer */
  let stickyObservers = [];
  const initSticky = () => {
    stickyObservers.forEach((o) => o.disconnect());
    stickyObservers = [];
    const sticky = document.querySelector('[data-hp-sticky-cta]');
    if (!sticky || !('IntersectionObserver' in window)) return;
    const heroCta = document.querySelector('[data-hp-hero-cta]');
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    let heroGone = !heroCta;
    let footerIn = false;
    const link = sticky.querySelector('a');
    const render = () => {
      const show = heroGone && !footerIn;
      sticky.classList.toggle('is-visible', show);
      document.documentElement.classList.toggle('hp-sticky-on', show && mobile.matches);
      sticky.setAttribute('aria-hidden', String(!show));
      if (link) link.tabIndex = show ? 0 : -1;
    };
    if (heroCta) {
      const o = new IntersectionObserver(([e]) => { heroGone = !e.isIntersecting && e.boundingClientRect.top < 0; render(); });
      o.observe(heroCta);
      stickyObservers.push(o);
    }
    if (footer) {
      const o = new IntersectionObserver(([e]) => { footerIn = e.isIntersecting; render(); });
      o.observe(footer);
      stickyObservers.push(o);
    }
    render();
  };

  /* Toast */
  let toast;
  let toastTimer;
  const showToast = (message) => {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'hp-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2400);
  };

  /* Quick add: POST /cart/add.js, then refresh the theme's cart drawer and cart count */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-hp-add]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();

    const card = btn.closest('.hp-card');
    const name = card ? card.querySelector('.hp-card__title').textContent.trim() : 'Item';
    const drawer = document.querySelector('cart-drawer');
    const body = new FormData();
    body.append('id', btn.dataset.variantId);
    body.append('quantity', '1');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.append('sections', drawer.getSectionsToRender().map((s) => s.id));
      body.append('sections_url', window.location.pathname);
    }

    btn.setAttribute('aria-busy', 'true');
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');

      if (drawer && data.sections && typeof drawer.renderContents1 === 'function') {
        try { drawer.renderContents1(data); } catch (err) { console.warn('hp-home: cart drawer refresh failed', err); }
      }
      // publish and PUB_SUB_EVENTS are top-level declarations in assets/pubsub.js and constants.js.
      // Source 'product-card' stops the drawer's cart items (assets/cre-cart.js) re-rendering from the cart page.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-card', updateType: 'add', variantId: btn.dataset.variantId, cartState: data });
      }

      btn.classList.add('is-added');
      btn.textContent = '✓';
      setTimeout(() => { btn.classList.remove('is-added'); btn.textContent = '+'; }, 1600);
      showToast(`${name} added to cart`);
    } catch (err) {
      showToast(err.message);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  /* Hero video: pause button, and no autoplay for people who ask for reduced motion */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initHeroVideo = (root) => {
    root.querySelectorAll('[data-hp-hero-video]').forEach((video) => {
      if (video.dataset.hpBound) return;
      video.dataset.hpBound = 'true';
      const btn = video.parentElement.querySelector('[data-hp-hero-pause]');
      const sync = () => {
        if (!btn) return;
        const paused = video.paused;
        btn.classList.toggle('is-paused', paused);
        btn.setAttribute('aria-pressed', String(paused));
        btn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      };
      if (video.dataset.posterMobile && mobile.matches) video.poster = video.dataset.posterMobile;
      if (reduceMotion.matches) { video.removeAttribute('autoplay'); video.pause(); }
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      if (btn) btn.addEventListener('click', () => { if (video.paused) video.play(); else video.pause(); });
      sync();
    });
  };

  /* Hero photo slider: dots, swipe (CSS scroll-snap), optional autoplay that pauses on hover,
     focus and touch, and never runs for reduced-motion users */
  const initHeroSlider = (root) => {
    root.querySelectorAll('[data-hp-slider]').forEach((slider) => {
      if (slider.dataset.hpBound) return;
      slider.dataset.hpBound = 'true';
      const track = slider.querySelector('[data-hp-slides]');
      const slides = [...slider.querySelectorAll('[data-hp-slide]')];
      const dots = [...slider.querySelectorAll('[data-hp-dot]')];
      if (slides.length < 2) return;
      let current = 0;
      const go = (i, smooth = true) => {
        current = (i + slides.length) % slides.length;
        track.scrollTo({ left: track.clientWidth * current, behavior: smooth ? 'smooth' : 'auto' });
      };
      const sync = () => {
        const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        current = i;
        dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      };
      track.addEventListener('scroll', () => { window.requestAnimationFrame(sync); }, { passive: true });
      dots.forEach((d, n) => d.addEventListener('click', () => { go(n); stop(); }));
      let timer = null;
      let paused = false;
      const seconds = Math.max(3, parseInt(slider.dataset.interval, 10) || 5);
      const start = () => {
        if (timer || paused || slider.dataset.autoplay !== 'true' || reduceMotion.matches) return;
        timer = setInterval(() => { if (!document.hidden) go(current + 1); }, seconds * 1000);
      };
      const stop = () => { clearInterval(timer); timer = null; };
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', () => { if (!paused) start(); });
      slider.addEventListener('focusin', stop);
      slider.addEventListener('touchstart', () => { paused = true; stop(); }, { passive: true });
      // Theme editor: show the photo block being edited and hold it there
      document.addEventListener('shopify:block:select', (e) => {
        const n = slides.indexOf(e.target.closest('[data-hp-slide]') || e.target);
        if (n > -1) { paused = true; stop(); go(n, false); }
      });
      document.addEventListener('shopify:block:deselect', () => { paused = false; start(); });
      window.addEventListener('resize', () => go(current, false));
      start();
    });
  };

  const init = (root = document) => { initRails(root); initSticky(); initHeroVideo(root); initHeroSlider(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  mobile.addEventListener('change', () => initRails(document));
})();
